import {
  Card,
  ConfigProvider,
  DatePicker,
  Result,
  Select,
  Space,
  Tabs,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import useApi from "../../utils/useApi";
import ConfigCard from "./ConfigCard";
import ConversationsCard from "./ConversationsCard";
import PromptCard from "./PromptCard";
import UsageCard from "./UsageCard";
import WhatsappCard from "./WhatsappCard";

const { RangePicker } = DatePicker;
const { Text } = Typography;
const JANELA_PADRAO_DIAS = 30;
const ABAS = ["conversas", "uso", "whatsapp", "configuracao", "prompt"];

export default function AiAgent() {
  const { user } = useAuth();
  const { getAgentCatalog } = useApi();
  const podeVer = Boolean(user?.is_superuser);
  const [periodo, setPeriodo] = useState([
    dayjs().subtract(JANELA_PADRAO_DIAS - 1, "day"),
    dayjs(),
  ]);
  const [clientId, setClientId] = useState(undefined);
  const [branchId, setBranchId] = useState(undefined);
  const [parametros, setParametros] = useSearchParams();
  const pedida = parametros.get("aba");
  const aba = ABAS.includes(pedida) ? pedida : "conversas";

  const { data: catalogo } = useQuery({
    queryKey: ["agent-catalog"],
    queryFn: getAgentCatalog,
    enabled: podeVer,
  });

  if (!podeVer) {
    return (
      <Result
        status="403"
        title="Acesso restrito"
        subTitle="Esta área é exclusiva para superadmins."
      />
    );
  }

  const filtros = {
    inicio: periodo?.[0]?.format("YYYY-MM-DD"),
    fim: periodo?.[1]?.format("YYYY-MM-DD"),
    client_id: clientId,
    branch_id: branchId,
  };

  const filiais = (catalogo?.filiais || []).filter(
    (f) => clientId === undefined || f.client_id === clientId
  );

  const usuarios = (catalogo?.usuarios || []).filter(
    (u) => clientId === undefined || u.client_id === clientId
  );

  const escolherCliente = (id) => {
    setClientId(id);
    setBranchId(undefined);
  };

  const barraDeFiltros = (
    <Card>
      <Space wrap align="center">
        <Text strong>Filtros</Text>
        <RangePicker
          value={periodo}
          format="DD/MM/YYYY"
          allowClear={false}
          onChange={(datas) => datas && setPeriodo(datas)}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Cliente"
          style={{ width: 220 }}
          value={clientId}
          options={(catalogo?.clientes || []).map((c) => ({
            value: c.id,
            label: c.name,
          }))}
          onChange={(v) => escolherCliente(v ?? undefined)}
        />
        <Select
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Filial"
          style={{ width: 240 }}
          value={branchId}
          options={filiais.map((f) => ({ value: f.id, label: f.name }))}
          onChange={(v) => setBranchId(v ?? undefined)}
        />
      </Space>
    </Card>
  );

  return (
    <ConfigProvider
      theme={{
        components: { Card: { headerBg: "var(--ouran-card-header-bg)" } },
      }}>
      <div
        style={{
          width: "100%",
          marginTop: 20,
          display: "flex",
          justifyContent: "center",
        }}>
        <div style={{ width: "94%" }}>
          <Tabs
            activeKey={aba}
            onChange={(chave) => setParametros({ aba: chave })}
            items={[
              {
                key: "conversas",
                label: "Conversas",
                children: (
                  <Space
                    direction="vertical"
                    size={20}
                    style={{ width: "100%" }}>
                    {barraDeFiltros}
                    <Card title="Conversas">
                      <ConversationsCard
                        filtros={filtros}
                        usuarios={usuarios}
                      />
                    </Card>
                  </Space>
                ),
              },
              {
                key: "uso",
                label: "Uso e custo",
                children: (
                  <Space
                    direction="vertical"
                    size={20}
                    style={{ width: "100%" }}>
                    {barraDeFiltros}
                    <Card title="Uso e custo">
                      <UsageCard
                        filtros={filtros}
                        onSelecionarCliente={escolherCliente}
                      />
                    </Card>
                  </Space>
                ),
              },
              {
                key: "whatsapp",
                label: "WhatsApp",
                children: <WhatsappCard />,
              },
              {
                key: "configuracao",
                label: "Configuração",
                children: <ConfigCard />,
              },
              {
                key: "prompt",
                label: "Prompt",
                children: <PromptCard />,
              },
            ]}
          />
        </div>
      </div>
    </ConfigProvider>
  );
}
